import { useTranslation } from 'react-i18next';
import { esDominioDeCorreoGenerico } from '@evo-hub/shared';
import { Modal } from './ui/Modal.js';
import { Button } from './ui/Button.js';

interface PropagationModalProps {
  open: boolean;
  onClose: () => void;
  fromEmail: string;
  actionType: 'category' | 'followup';
  /** category string for 'category' type; boolean (true=mark) for 'followup' type */
  newValue: string | boolean;
  onThisOnly: () => void;
  onSender: () => void;
  onDomain: () => void;
  isPending?: boolean;
}

export function PropagationModal({
  open, onClose, fromEmail, actionType, newValue,
  onThisOnly, onSender, onDomain, isPending,
}: PropagationModalProps) {
  const { t } = useTranslation();

  const domain = fromEmail.split('@')[1] ?? fromEmail;
  // gmail.com, outlook.com…: "todo el dominio" sería todo el que use ese
  // correo. No se ofrece (y el servidor lo rechaza igual).
  const dominioGenerico = esDominioDeCorreoGenerico(domain);
  const isFollowup = actionType === 'followup';
  const isMarkFollowup = newValue === true;
  const tone: 'amber' | 'cyan' = isFollowup ? 'amber' : 'cyan';

  const title = isFollowup
    ? (isMarkFollowup ? t('email.followupToggle.modalTitleMark') : t('email.followupToggle.modalTitleRemove'))
    : t('email.categoryModal.title', { category: newValue as string });

  const description = isFollowup
    ? fromEmail
    : t('email.categoryModal.subtitle', { sender: fromEmail });

  const prompt = isFollowup
    ? (isMarkFollowup ? t('email.followupToggle.learnPrompt') : t('email.followupToggle.learnPromptRemove'))
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      preventClose={isPending ?? false}
      footer={
        <Button
          variant="ghost"
          tone="muted"
          size="sm"
          disabled={isPending ?? false}
          onClick={onClose}
          fullWidth
        >
          {t('common.cancel')}
        </Button>
      }
    >
      {prompt && (
        <p style={{ fontSize: 12, color: 'var(--evo-fg-3)', margin: '0 0 12px 0', fontFamily: 'var(--evo-font)' }}>
          {prompt}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button
          variant="outline"
          tone="muted"
          size="md"
          disabled={isPending ?? false}
          onClick={onThisOnly}
          fullWidth
        >
          {isFollowup ? t('email.followupToggle.thisEmailOnly') : t('email.categoryModal.thisEmailOnly')}
        </Button>
        <Button
          variant="solid"
          tone={tone}
          size="md"
          disabled={isPending ?? false}
          onClick={onSender}
          fullWidth
        >
          {isFollowup ? t('email.followupToggle.yesSender') : t('email.categoryModal.allSender')}
        </Button>
        {dominioGenerico ? (
          <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', margin: 0, fontFamily: 'var(--evo-font)' }}>
            {t('email.categoryModal.genericDomain', { domain })}
          </p>
        ) : (
          <Button
            variant="outline"
            tone={tone}
            size="md"
            disabled={isPending ?? false}
            onClick={onDomain}
            fullWidth
          >
            {isFollowup ? t('email.followupToggle.yesDomain') : t('email.categoryModal.allDomain', { domain })}
          </Button>
        )}
      </div>
    </Modal>
  );
}
